"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
const ThemeContext = createContext(null);
/**
 * Read the active theme + density. Returns `null` when used outside a
 * `ThemeProvider` so consumers can degrade gracefully.
 */
export function useTheme() {
    return useContext(ThemeContext);
}
const DEFAULT_STORAGE_KEY = "helm.theme";
const DEFAULT_DENSITY_KEY = "helm.density";
function readStored(key, fallback) {
    if (typeof window === "undefined")
        return fallback;
    try {
        const value = window.localStorage.getItem(key);
        return value ?? fallback;
    }
    catch {
        return fallback;
    }
}
function writeStored(key, value) {
    if (typeof window === "undefined")
        return;
    try {
        window.localStorage.setItem(key, value);
    }
    catch {
        // localStorage may be blocked (private mode, quota) — no-op.
    }
}
function detectSystemMode() {
    if (typeof window === "undefined" || !window.matchMedia)
        return "dark";
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}
export function ThemeProvider({ children, defaultPreference = "auto", defaultDensity = "compact", persist = true, storageKey = DEFAULT_STORAGE_KEY, densityStorageKey = DEFAULT_DENSITY_KEY, target, }) {
    const [preference, setPreferenceState] = useState(() => {
        if (!persist)
            return defaultPreference;
        const stored = readStored(storageKey, defaultPreference);
        if (stored === "light" || stored === "dark" || stored === "auto")
            return stored;
        return defaultPreference;
    });
    const [density, setDensityState] = useState(() => {
        if (!persist)
            return defaultDensity;
        const stored = readStored(densityStorageKey, defaultDensity);
        return stored === "comfortable" ? "comfortable" : "compact";
    });
    const [systemMode, setSystemMode] = useState(detectSystemMode);
    // Mirror system preference for `auto` mode.
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia)
            return undefined;
        const mql = window.matchMedia("(prefers-color-scheme: light)");
        const handler = (event) => {
            setSystemMode(event.matches ? "light" : "dark");
        };
        if (typeof mql.addEventListener === "function") {
            mql.addEventListener("change", handler);
            return () => mql.removeEventListener("change", handler);
        }
        mql.addListener(handler);
        return () => mql.removeListener(handler);
    }, []);
    const resolvedMode = preference === "auto" ? systemMode : preference;
    // Apply data-theme + data-density to the target element.
    useEffect(() => {
        if (typeof document === "undefined")
            return;
        const element = target ?? document.documentElement;
        if (preference === "auto") {
            element.removeAttribute("data-theme");
        }
        else {
            element.setAttribute("data-theme", preference);
        }
        element.setAttribute("data-density", density);
    }, [preference, density, target]);
    const setPreference = useCallback((next) => {
        setPreferenceState(next);
        if (persist)
            writeStored(storageKey, next);
    }, [persist, storageKey]);
    const setDensity = useCallback((next) => {
        setDensityState(next);
        if (persist)
            writeStored(densityStorageKey, next);
    }, [persist, densityStorageKey]);
    const toggleMode = useCallback(() => {
        setPreference(resolvedMode === "light" ? "dark" : "light");
    }, [resolvedMode, setPreference]);
    const value = useMemo(() => ({ preference, resolvedMode, density, setPreference, setDensity, toggleMode }), [preference, resolvedMode, density, setPreference, setDensity, toggleMode]);
    return _jsx(ThemeContext.Provider, { value: value, children: children });
}
